import React, { useState, useEffect, useMemo, memo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, 
  Zap, 
  Target, 
  CheckCircle, 
  X, 
  ArrowUpRight, 
  Sparkles 
} from 'lucide-react';
import PropTypes from 'prop-types';

// Move static data outside component to prevent re-creation
const SECTIONS = [
  {
    title: 'What It Is',
    icon: <Brain className="w-6 h-6" />,
    desc: 'Not another analytics dashboard. This is the brain of your business — quietly watching how people interact with your website, ads, and social content, then translating that chaos into clear steps that actually grow revenue.',
    color: 'from-rose-500 to-pink-600',
    bgColor: 'from-rose-50 to-pink-50',
    textColor: 'text-rose-700',
    borderColor: 'border-rose-200',
    accentColor: 'bg-rose-500',
    details: [
      'Understands your entire funnel from first click to final purchase.',
      'Acts like a digital growth strategist, not just a data reporter.',
      'No fluff: only tells you what will make a real difference today.'
    ],
    highlight: '"From chaos to clarity: actionable steps to grow your revenue."',
    position: 'large',
    stat: '3x',
    statLabel: 'Revenue Growth'
  },
  {
    title: 'How It Works',
    icon: <Zap className="w-6 h-6" />,
    desc: 'It listens to your data the way a doctor listens to your heartbeat, scanning for the small problems before they become expensive ones, then giving you fixes you can apply instantly.',
    color: 'from-emerald-500 to-teal-600',
    bgColor: 'from-emerald-50 to-teal-50',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-200',
    accentColor: 'bg-emerald-500',
    details: [
      'Tracks drop-offs, silent pages, abandoned carts, ignored posts.',
      'Prioritizes what to fix first, based on impact, not noise.',
      'Every recommendation comes with a "why" and a "do this now."'
    ],
    highlight: '"Finds the leaks in your funnel before they cost you money."',
    position: 'tall',
    stat: '24/7',
    statLabel: 'Monitoring'
  },
  {
    title: 'Pain It Solves',
    icon: <Target className="w-6 h-6" />,
    desc: 'You\'re drowning in dashboards and still don\'t know what\'s actually broken. This engine ends the guesswork — it shows you exactly where money is leaking and how to stop it.',
    color: 'from-violet-500 to-purple-600',
    bgColor: 'from-violet-50 to-purple-50',
    textColor: 'text-violet-700',
    borderColor: 'border-violet-200',
    accentColor: 'bg-violet-500',
    details: [
      'No more guessing where leads disappear, it shows you the exact leak.',
      'Replaces endless analytics scrolling with direct answers.',
      'Removes dependency on expensive consultants and messy spreadsheets.'
    ],
    highlight: '"Stop guessing. Start fixing with precise, money-saving insights."',
    position: 'wide',
    stat: '100%',
    statLabel: 'Clarity'
  },
  {
    title: 'Why It\'s Valuable',
    icon: <CheckCircle className="w-6 h-6" />,
    desc: 'Because knowing isn\'t enough,action is. This system turns hidden data into decisions that lead to more leads, more sales, and fewer sleepless nights wondering "what went wrong?".',
    color: 'from-amber-500 to-orange-600',
    bgColor: 'from-amber-50 to-orange-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
    accentColor: 'bg-amber-500',
    details: [
      'Gives you measurable growth — not vanity metrics.',
      'One-click fixes or automated improvements when you\'re busy.',
      'Feels like having a 24/7 marketing expert that never burns out.'
    ],
    highlight: '"Turn data into dollars with decisions that drive growth."',
    position: 'medium',
    stat: '10x',
    statLabel: 'ROI'
  }
];

// Memoized grid classes to prevent re-creation
const GRID_CLASSES = [
  'col-span-7 row-span-7',
  'col-span-5 row-span-12',
  'col-span-4 row-span-5',
  'col-span-3 row-span-5'
];

// Memoized animation variants
const createOrbVariants = (prefersReducedMotion) => ({
  animate: prefersReducedMotion ? {} : { 
    scale: [1, 1.2, 1], 
    x: [0, 30, 0], 
    y: [0, -20, 0]
  },
  transition: { duration: 20, repeat: Infinity, ease: 'easeInOut' }
});

const SmartRecommendationEngine = () => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Memoized orb variants
  const orbVariants = useMemo(
    () => createOrbVariants(prefersReducedMotion),
    [prefersReducedMotion]
  );

  // Memoized particle positions to prevent recalculation
  const particles = useMemo(() => {
    if (prefersReducedMotion) return [];
    return Array.from({ length: 6 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      duration: 8 + Math.random() * 5,
      delay: i * 0.3
    }));
  }, [prefersReducedMotion]);

  // Memoized event handlers
  const handleCardHover = useCallback((idx) => {
    setHoveredCard(idx);
  }, []);

  const handleCardLeave = useCallback(() => {
    setHoveredCard(null);
  }, []);

  const handleCardClick = useCallback((section) => {
    setSelectedCard(section);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedCard(null);
  }, []);

  // Memoized card components to prevent unnecessary re-renders
  const renderedCards = useMemo(() => 
    SECTIONS.map((section, idx) => (
      <MemoizedCard 
        key={section.title} // Use stable key
        section={section}
        idx={idx}
        hoveredCard={hoveredCard}
        prefersReducedMotion={prefersReducedMotion}
        onHover={handleCardHover}
        onLeave={handleCardLeave}
        onClick={handleCardClick}
        gridClass={GRID_CLASSES[idx]}
        layout={section.position}
      />
    )),
    [hoveredCard, prefersReducedMotion, handleCardHover, handleCardLeave, handleCardClick]
  );

  const mobileCards = useMemo(() => 
    SECTIONS.map((section, idx) => (
      <MemoizedMobileCard 
        key={section.title}
        section={section}
        idx={idx}
        hoveredCard={hoveredCard}
        prefersReducedMotion={prefersReducedMotion}
        onHover={handleCardHover}
        onLeave={handleCardLeave}
        onClick={handleCardClick}
        layout="mobile"
      />
    )),
    [hoveredCard, prefersReducedMotion, handleCardHover, handleCardLeave, handleCardClick]
  );

  return (
    <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-white via-gray-50 to-white">
      {/* Optimized: Reduced orbs from 3 to 2, simplified animations */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-60 -left-60 w-96 h-96 bg-gradient-to-br from-pink-300 via-rose-400 to-pink-500 rounded-full blur-3xl opacity-20 will-change-transform"
          animate={orbVariants.animate}
          transition={orbVariants.transition}
        />
        <motion.div
          className="absolute bottom-20 left-1/3 w-96 h-96 bg-gradient-to-br from-emerald-300 via-teal-400 to-cyan-500 rounded-full blur-3xl opacity-20 will-change-transform"
          animate={orbVariants.animate}
          transition={{ ...orbVariants.transition, delay: 2 }}
        />
      </div>

      {/* Optimized: Reduced particles from 12 to 6, simplified animations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map(({ id, left, top, duration, delay }) => (
          <motion.div
            key={id}
            className="absolute w-2 h-2 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full opacity-20 will-change-transform"
            style={{ left, top }}
            animate={{
              y: [0, -50, 0],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration,
              repeat: Infinity,
              delay,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10 pt-16 lg:pt-24 px-4 md:px-8 pb-16">
        {/* Optimized Header - Removed floating icons for performance */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 30 }}
          animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-6xl mx-auto"
        >
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.9 }}
            animate={prefersReducedMotion ? false : { opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="inline-block mb-6"
          >
            <div className="relative inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-purple-100 to-rose-100 border-2 border-purple-300 shadow-lg">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-bold text-purple-700 tracking-wide uppercase">
                AI-Powered Insights
              </span>
            </div>
          </motion.div>

          <motion.h1 
            className="text-5xl md:text-7xl font-black mb-6 leading-tight tracking-tight"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
            animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className="block text-gray-900">Smart</span>
            <span className="block bg-gradient-to-r from-pink-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
              Recommendation
            </span>
            <span className="block text-gray-900">Engine</span>
          </motion.h1>
          
          <motion.p 
            className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed font-medium"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
            animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Feel the <span className="text-purple-600 font-black">relief</span> of clear, actionable insights that turn your data into{' '}
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent font-black">growth</span>
            —click any card to dive deeper.
          </motion.p>

          <motion.div
            className="mt-6 mx-auto w-24 h-1 bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500 rounded-full"
            initial={prefersReducedMotion ? false : { scaleX: 0, opacity: 0 }}
            animate={prefersReducedMotion ? false : { scaleX: 1, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          />
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0 }}
          animate={prefersReducedMotion ? false : { opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="max-w-7xl mx-auto"
        >
          {/* Desktop Grid */}
          <div className="hidden lg:grid grid-cols-12 grid-rows-12 gap-6 h-[800px]">
            {renderedCards}
          </div>

          {/* Mobile Grid */}
          <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-6">
            {mobileCards}
          </div>
        </motion.div>

        {/* Optimized Bottom Badge - Removed animated circles */}
        <motion.div
          initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
          animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mt-16"
        >
          <div className="inline-flex items-center gap-4 px-8 py-4 rounded-full bg-white border-2 border-gray-200 shadow-lg">
            <p className="text-gray-800 text-base font-semibold">
              Powered by <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent font-bold">AI</span> — Real-time insights
            </p>
          </div>
        </motion.div>
      </div>

      {/* Optimized Modal */}
      <AnimatePresence>
        {selectedCard && (
          <ModalContent 
            selectedCard={selectedCard}
            onClose={handleCloseModal}
            prefersReducedMotion={prefersReducedMotion}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

// Memoized Card Component for Desktop
const MemoizedCard = memo(({ section, idx, hoveredCard, prefersReducedMotion, onHover, onLeave, onClick, gridClass, layout }) => (
  <motion.div
    initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.95 }}
    animate={prefersReducedMotion ? false : { opacity: 1, scale: 1 }}
    transition={{ duration: 0.5, delay: 0.1 * idx }}
    onMouseEnter={() => onHover(idx)}
    onMouseLeave={onLeave}
    whileHover={prefersReducedMotion ? false : { scale: 1.01, y: -4 }}
    onClick={() => onClick(section)}
    className={`${gridClass} group relative cursor-pointer`}
    role="button"
    tabIndex={0}
  >
    <CardContent 
      section={section} 
      hoveredCard={hoveredCard} 
      idx={idx} 
      prefersReducedMotion={prefersReducedMotion} 
      layout={layout} 
    />
  </motion.div>
));

MemoizedCard.displayName = 'MemoizedCard';

// Memoized Mobile Card Component
const MemoizedMobileCard = memo(({ section, idx, hoveredCard, prefersReducedMotion, onHover, onLeave, onClick, layout }) => (
  <motion.div
    initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
    animate={prefersReducedMotion ? false : { opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: 0.1 * idx }}
    onMouseEnter={() => onHover(idx)}
    onMouseLeave={onLeave}
    whileHover={prefersReducedMotion ? false : { y: -6 }}
    onClick={() => onClick(section)}
    className="group relative cursor-pointer"
    role="button"
    tabIndex={0}
  >
    <CardContent 
      section={section} 
      hoveredCard={hoveredCard} 
      idx={idx} 
      prefersReducedMotion={prefersReducedMotion} 
      layout={layout} 
    />
  </motion.div>
));

MemoizedMobileCard.displayName = 'MemoizedMobileCard';

// Optimized CardContent Component
const CardContent = memo(({ section, hoveredCard, idx, prefersReducedMotion, layout }) => {
  return (
    <div className={`relative h-full bg-gradient-to-br ${section.bgColor} rounded-2xl p-6 lg:p-8 border-2 ${section.borderColor} hover:border-gray-300 transition-colors duration-300 flex flex-col shadow-lg group-hover:shadow-xl`}>
      {/* Optimized overlay animation */}
      <motion.div
        className={`absolute inset-0 bg-gradient-to-br ${section.color} opacity-0`}
        animate={{ opacity: hoveredCard === idx ? 0.08 : 0 }}
        transition={{ duration: 0.2 }}
      />

      <div className="relative z-10 flex flex-col h-full">
        {/* Stat badge */}
        <div className="absolute -top-2 -right-2 px-3 py-1.5 bg-gray-900 text-white rounded-lg shadow-md">
          <div className="text-sm font-bold">{section.stat}</div>
        </div>

        {/* Icon */}
        <div className={`mb-4 w-16 h-16 rounded-2xl ${section.accentColor} flex items-center justify-center shadow-lg`}>
          {section.icon}
        </div>

        {/* Title */}
        <h3 className={`font-black mb-3 text-gray-900 ${
          layout === 'large' ? 'text-2xl lg:text-3xl' : 
          layout === 'tall' ? 'text-xl lg:text-2xl' : 'text-lg'
        }`}>
          {section.title}
        </h3>

        {/* Description */}
        <p className={`text-gray-700 mb-4 flex-grow font-medium ${
          layout === 'large' ? 'text-base' : 'text-sm'
        }`}>
          {section.desc}
        </p>

        {/* Details - only show for certain layouts */}
        {layout !== 'medium' && (
          <div className="flex flex-wrap gap-2 mb-4">
            {section.details.slice(0, layout === 'wide' ? 2 : 3).map((detail, dIdx) => (
              <span
                key={dIdx}
                className={`px-3 py-1.5 rounded-lg bg-white/90 ${section.textColor} text-xs font-semibold border ${section.borderColor}`}
              >
                {detail}
              </span>
            ))}
          </div>
        )}

        {/* Highlight */}
        <div className={`rounded-xl bg-white/80 p-4 border ${section.borderColor} shadow-md`}>
          <div className={`text-center ${section.textColor} italic font-semibold ${
            layout === 'large' ? 'text-sm' : 'text-xs'
          }`}>
            {section.highlight}
          </div>
        </div>

        {/* Progress bar animation */}
        <motion.div
          className={`mt-4 h-1 ${section.accentColor} rounded-full`}
          initial={false}
          animate={{ scaleX: hoveredCard === idx ? 1 : 0 }}
          style={{ transformOrigin: 'left' }}
          transition={{ duration: 0.3 }}
        />

        {/* Explore link */}
        <div className="flex items-center gap-2 mt-4 text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity text-gray-900">
          <span>Explore details</span>
          <ArrowUpRight className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
});

CardContent.displayName = 'CardContent';
CardContent.propTypes = {
  section: PropTypes.object.isRequired,
  hoveredCard: PropTypes.number,
  idx: PropTypes.number.isRequired,
  prefersReducedMotion: PropTypes.bool.isRequired,
  layout: PropTypes.string.isRequired
};

// Separate Modal Component for better performance
const ModalContent = memo(({ selectedCard, onClose, prefersReducedMotion }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
  >
    <motion.div
      initial={prefersReducedMotion ? false : { scale: 0.9, y: 30 }}
      animate={prefersReducedMotion ? false : { scale: 1, y: 0 }}
      exit={prefersReducedMotion ? false : { scale: 0.9, y: 30 }}
      transition={{ duration: 0.3 }}
      onClick={(e) => e.stopPropagation()}
      className="relative max-w-4xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
        aria-label="Close modal"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="p-8">
        {/* Stat badge */}
        <div className="absolute top-6 left-6 px-4 py-2 bg-gray-900 text-white rounded-xl">
          <div className="text-2xl font-black">{selectedCard.stat}</div>
          <div className="text-xs font-semibold opacity-80">{selectedCard.statLabel}</div>
        </div>

        {/* Icon */}
        <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl ${selectedCard.accentColor} shadow-xl mb-6`}>
          {selectedCard.icon}
        </div>

        {/* Title */}
        <h3 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">
          {selectedCard.title}
        </h3>

        {/* Description */}
        <p className="text-gray-600 text-lg mb-8 leading-relaxed">
          {selectedCard.desc}
        </p>

        {/* Highlight */}
        <div className={`bg-gradient-to-br ${selectedCard.bgColor} rounded-2xl p-6 border-2 ${selectedCard.borderColor} mb-6`}>
          <div className={`text-center ${selectedCard.textColor} text-xl italic font-black`}>
            {selectedCard.highlight}
          </div>
        </div>

        {/* Details */}
        <div className="flex flex-wrap gap-3">
          {selectedCard.details.map((detail, dIdx) => (
            <span
              key={dIdx}
              className={`px-4 py-2 rounded-xl bg-gradient-to-br ${selectedCard.bgColor} ${selectedCard.textColor} border-2 ${selectedCard.borderColor} font-semibold`}
            >
              {detail}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  </motion.div>
));

ModalContent.displayName = 'ModalContent';
ModalContent.propTypes = {
  selectedCard: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  prefersReducedMotion: PropTypes.bool.isRequired
};

export default SmartRecommendationEngine;